import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Chip,
    CircularProgress,
    Alert as MuiAlert,
    IconButton,
    Badge,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Button,
} from "@mui/material";
import {
    Close,
    ChevronLeft,
    ChevronRight,
    Notifications,
} from "@mui/icons-material";
import useQuery from "../../../../shared/components/hooks/useQuery";
import useMutation from "../../../../shared/components/hooks/useMutation";
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import moment from "moment";
import { useSosSocket } from "../../../../contexts/SosSocketContext";
import { SnackBarAlertContext } from "../../../../contexts/SnackbarContext";
import request from "../../../../request";

interface AlertData {
    id: string;
    priority: string;
    message: string;
    type: string;
    status?: string;
    acknowledgedDate?: string | null;
    acknowledgedTime?: string | null;
    source: string;
    employeeID?: string | number | null;
    gender?: string | null;
    location: string;
    createdAt: string;
    trip: any;
}

export type AlertFilter =
    | "normal"
    | "person-sos"
    | "vehicle-sos"
    | "safe-home";

interface AlertsSidebarProps {
    onCollapseChange?: (isCollapsed: boolean) => void;
    onClose?: () => void;
    onUnacknowledgedSosChange?: (state: {
        person: boolean;
        vehicle: boolean;
    }) => void;
    filter: AlertFilter;
    search?: string;
    fromDate?: string;
    toDate?: string;
    employeeSosOnly?: boolean;
    hideHeader?: boolean;
}

const AlertsSidebar: React.FC<AlertsSidebarProps> = ({
    onCollapseChange,
    onClose,
    onUnacknowledgedSosChange,
    filter,
    search = "",
    fromDate = "",
    toDate = "",
    employeeSosOnly = false,
    hideHeader = false,
}) => {
    const { set: showSnackbar } = useContext(SnackBarAlertContext);
    const [, setSessionVersion] = useState(0);
    useEffect(() => {
        const refreshSession = () => setSessionVersion((version) => version + 1);
        window.addEventListener("userDataUpdated", refreshSession);
        window.addEventListener("storage", refreshSession);
        return () => {
            window.removeEventListener("userDataUpdated", refreshSession);
            window.removeEventListener("storage", refreshSession);
        };
    }, []);
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const notificationsEnabled = userData?.userType !== "SuperAdmin";
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { loading, modifyData } = useMutation("");
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<AlertData | null>(null);
    const [comments, setComments] = useState("");
    const [errors, setErrors] = useState(false);
    const [femaleEmployeeSosOnly, setFemaleEmployeeSosOnly] = useState(false);
    const [employeeGenders, setEmployeeGenders] = useState<Record<string, string>>({});
    const requestedEmployeeGenderIds = useRef<Set<string>>(new Set());
    const { lastSosAlert } = useSosSocket();
    const [cordonItems, setCordonItems] = useState<any[]>([]);
    const [cordonLoaded, setCordonLoaded] = useState(false);
    const [polledAlertsData, setPolledAlertsData] = useState<any>(null);
    const [acknowledgedCordonIds, setAcknowledgedCordonIds] = useState<Set<string>>(
        () => new Set((localStorage.getItem("acknowledgedCordonSosIds") || "").split("|").filter(Boolean)),
    );

    // Fetch alerts from admin API
    const {
        loading: alertsLoading,
        data: alertsData,
        error: alertsError,
        fetchData
    } = useQuery("/admin/alert", {
        params: {
            take: 100,
        },
    }, null, notificationsEnabled);
    const pollAlertsRef = useRef<() => void>(() => {});
    pollAlertsRef.current = () => {
        if (!notificationsEnabled) return;
        request
            .get("/admin/alert", {
                params: {
                    take: 100,
                },
            })
            .then((response) => setPolledAlertsData(response.data))
            .catch((error) => {
                console.warn("Failed to check for new alerts:", error);
            });
    };

    useEffect(() => {
        if (!notificationsEnabled) return;
        const intervalId = window.setInterval(() => {
            pollAlertsRef.current();
            request
                .get("/admin/alert/cordon-panic")
                .then((response) => {
                    setCordonItems(response.data?.items ?? []);
                })
                .catch(() => {
                    // Some roles are not permitted to read Cordon panic alerts.
                });
        }, 30000);

        return () => window.clearInterval(intervalId);
    }, []);

    useEffect(() => {
        if(lastSosAlert) {
            fetchData();
            pollAlertsRef.current();
        }
    }, [lastSosAlert]);

    useEffect(() => {
        if (!notificationsEnabled) return;
        let cancelled = false;
        request
            .get("/admin/alert/cordon-panic")
            .then((response) => {
                if (!cancelled) {
                    setCordonItems(response.data?.items ?? []);
                    setCordonLoaded(true);
                }
            })
            .catch(() => {
                // Expected for roles without Alerts-Read (403) or any other failure —
                // degrade silently, DB alerts still render.
                if (!cancelled) {
                    setCordonItems([]);
                    setCordonLoaded(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Transform API data to component format
    const transformAlerts = (apiData: any): AlertData[] => {
        if (!apiData?.items) return [];

        return apiData?.items?.map((alert: any): AlertData => {
            // Determine priority based on alert type
            let priority = "Low";
            if (["emergency", "breakdown", "accident", "sos"].includes(alert?.type)) {
                priority = "High";
            } else if (["delay"].includes(alert?.type)) {
                priority = "Medium";
            }

            // Create meaningful message
            let message = alert?.remarks ?? "";

            // Add context from trip and source
            if (alert.trip) {
                message += ` (Route #${alert?.trip?.routeNumber}), (TripID ${ alert?.tripID})`;
            }

            if (alert?.source === "Driver" && alert?.driver) {
                message += ` - Driver: ${alert?.driver?.name}`;
            } else if (alert?.source === "Employee" && alert?.employee) {
                message += ` - Employee: ${alert?.employee?.name}`;
            } else if (alert?.source === "System Generated") {
                message += ` - Driver: ${
                    alert?.trip?.driver?.name
                }

                
                ${
                alert.trip?.driver?.code ? 
                ` (${alert?.trip?.driver?.code})`
                
                
                : ""
              
                }, Vendor: ${
                    alert?.trip?.vendor?.name
                    
                } (${alert?.trip?.vendor?.code}), Trip Date: ${
               alert?.trip?.date ? moment(alert?.trip?.date).format("DD-MM-YYYY") : ""
            }`;
             }
            return {
                id: alert.id,
                priority,
                message,
                type: alert.type,
                status: alert.status,
                acknowledgedDate: alert.acknowledgedDate,
                acknowledgedTime: alert.acknowledgedTime,
                source: alert.source,
                employeeID: alert.employeeID ?? alert.employee?.id,
                gender: alert.employee?.gender ?? alert.gender,
                location: alert.location,
                createdAt: alert.createdAt,
                trip: alert.trip,
            };
        });
    };

    // Cordon panics use "YYYY-MM-DD HH:mm:ss" in Asia/Kolkata wall-clock time
    // with no offset marker (backend: alert.service.ts:423-424); DB alerts use
    // standard ISO 8601 UTC. Normalize both to real epoch millis before compare.
    const getAlertEpoch = (createdAt: string): number => {
        const isCordonFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(createdAt);
        const normalized = isCordonFormat ? createdAt.replace(" ", "T") + "+05:30" : createdAt;
        return moment(normalized).valueOf();
    };

    const transformedAlerts = transformAlerts({
        items: [
            ...(polledAlertsData?.items ?? alertsData?.items ?? []),
            ...cordonItems.map((alert) => ({
                ...alert,
                status: acknowledgedCordonIds.has(alert.id) ? "Closed" : alert.status,
            })),
        ],
    }).sort((a, b) => getAlertEpoch(b.createdAt) - getAlertEpoch(a.createdAt));
    const employeesMissingGender = Array.from(
        new Set(
            transformedAlerts
                .filter(
                    (alert) =>
                        alert.source?.trim().toLowerCase() === "employee" &&
                        !alert.gender &&
                        alert.employeeID != null,
                )
                .map((alert) => String(alert.employeeID)),
        ),
    ).sort();
    const employeesMissingGenderKey = employeesMissingGender.join("|");
    useEffect(() => {
        employeesMissingGender
            .filter((id) => !requestedEmployeeGenderIds.current.has(id))
            .forEach((id) => {
                requestedEmployeeGenderIds.current.add(id);
                request
                    .get(`/admin/employee/${id}`)
                    .then((response) => {
                        const gender =
                            response.data?.data?.gender ?? response.data?.gender;
                        if (gender) {
                            setEmployeeGenders((current) => ({
                                ...current,
                                [id]: String(gender),
                            }));
                        }
                    })
                    .catch((error) => {
                        console.warn(
                            `Failed to load gender for employee ${id}:`,
                            error,
                        );
                    });
            });
    }, [employeesMissingGenderKey]);
    const getAlertGender = (alert: AlertData) =>
        String(
            alert.gender ??
                (alert.employeeID != null
                    ? employeeGenders[String(alert.employeeID)]
                    : "") ??
                "",
        )
            .trim()
            .toLowerCase();
    const visibleAlerts = transformedAlerts.filter((alert) => {
        const isSos = alert.type?.toLowerCase() === "sos";
        const normalizedType = alert.type?.toLowerCase().replace(/[\s_-]/g, "");
        const isSafeHome = normalizedType === "safehome";
        if (filter === "normal") return !isSos && !isSafeHome;
        if (filter === "safe-home") return isSafeHome;
        if (!isSos) return false;
        if (filter === "person-sos") {
            return ["driver", "employee"].includes(alert.source?.toLowerCase());
        }
        return alert.source?.toLowerCase() === "cordon";
    });
    const searchText = search.trim().toLowerCase();
    const searchFilteredAlerts =
        searchText || fromDate || toDate
            ? visibleAlerts.filter((alert) => {
                const alertDate = moment(alert.createdAt).format("YYYY-MM-DD");
                const matchesDate =
                    (!fromDate || alertDate >= fromDate) &&
                    (!toDate || alertDate <= toDate);
                if (!matchesDate) return false;
                if (!searchText) return true;
                const searchableText = [
                    alert.message,
                    alert.source,
                    alert.employeeID,
                    alert.trip?.id,
                    alert.trip?.tripID,
                    alert.trip?.routeNumber,
                    alert.trip?.driver?.name,
                    alert.trip?.employee?.name,
                    alert.employeeID,
                ].filter((value) => value != null).join(" ").toLowerCase();
                return searchableText.includes(searchText);
            })
            : visibleAlerts;
    const displayedAlerts =
        employeeSosOnly
            ? searchFilteredAlerts.filter(
                (alert) =>
                    alert.source?.trim().toLowerCase() === "employee",
            )
            : filter === "person-sos" && femaleEmployeeSosOnly
            ? visibleAlerts.filter(
                  (alert) =>
                      alert.source?.trim().toLowerCase() === "employee" &&
                      getAlertGender(alert) === "female",
              )
            : searchFilteredAlerts;
    const transformedAlertsRef = useRef(transformedAlerts);
    transformedAlertsRef.current = transformedAlerts;
    const knownAlertIds = useRef<Set<string> | null>(null);

    useEffect(() => {
        if ((!alertsData && !polledAlertsData) || !cordonLoaded) return;

        const currentAlerts = transformedAlertsRef.current;
        const currentAlertIds = currentAlerts.map(
            (alert) => `${alert.type}:${alert.id}`,
        );
        if (knownAlertIds.current === null) {
            knownAlertIds.current = new Set(currentAlertIds);
            return;
        }

        const newAlerts = currentAlerts.filter(
            (alert) => !knownAlertIds.current?.has(`${alert.type}:${alert.id}`),
        );
        currentAlertIds.forEach((id) => knownAlertIds.current?.add(id));

        const newAlert = newAlerts[0];
        if (newAlert) {
            const severity =
                newAlert.priority === "High"
                    ? "error"
                    : newAlert.priority === "Medium"
                      ? "warning"
                      : "info";
            showSnackbar({
                status: true,
                type: severity,
                message: `${newAlert.type.toUpperCase()} alert: ${newAlert.message}`,
            });
        }
    }, [alertsData, polledAlertsData, cordonItems, cordonLoaded, showSnackbar]);

    const isAcknowledged = (alert: AlertData) =>
        alert.status?.toLowerCase() === "closed" ||
        Boolean(alert.acknowledgedDate || alert.acknowledgedTime);
    const hasUnacknowledgedPersonSos = transformedAlerts.some((alert) => {
        if (
            alert.type?.toLowerCase() !== "sos" ||
            !["driver", "employee"].includes(alert.source?.toLowerCase())
        ) return false;
        return !isAcknowledged(alert);
    });
    const hasUnacknowledgedVehicleSos = transformedAlerts.some((alert) => {
        if (
            alert.type?.toLowerCase() !== "sos" ||
            alert.source?.toLowerCase() !== "cordon"
        ) return false;
        return !isAcknowledged(alert);
    });

    useEffect(() => {
        onUnacknowledgedSosChange?.({
            person: hasUnacknowledgedPersonSos,
            vehicle: hasUnacknowledgedVehicleSos,
        });
    }, [
        hasUnacknowledgedPersonSos,
        hasUnacknowledgedVehicleSos,
        onUnacknowledgedSosChange,
    ]);

    // Handle collapse/expand toggle
    const handleToggleCollapse = () => {
        const newCollapsedState = !isCollapsed;
        setIsCollapsed(newCollapsedState);
        if (onCollapseChange) {
            onCollapseChange(newCollapsedState);
        }
    };

    // Format time for display
    const formatTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get priority color
    const getPriorityColor = (priority: string): string => {
        switch (priority) {
            case "High":
                return "#ffcdd2";
            case "Medium":
                return "#fff9c4";
            case "Low":
                return "#e0f7fa";
            default:
                return "#f5f5f5";
        }
    };

    // Get type chip color
    const getTypeChipColor = (type: string) => {
        switch (type) {
            case "emergency":
                return "error";
            case "breakdown":
                return "error";
            case "accident":
                return "error";
            case "sos":
                return "error";
            case "delay":
                return "warning";
            case "alert":
                return "info";
            default:
                return "default";
        }
    };

    // Count high priority alerts for badge
    const highPriorityCount = displayedAlerts.filter(alert => alert.priority === "High").length;
    const totalAlertsCount = displayedAlerts.length;

    // Collapsed view
    if (isCollapsed) {
        return (
            <Box
                sx={{
                    width: "64px",
                    height: "100%",
                    minHeight: 0,
                    background: "linear-gradient(180deg, rgba(60,97,139,0.24) 0%, rgba(60,97,139,0.12) 100%)",
                    display: "flex",
                    borderTopLeftRadius: "26px",
                    borderBottomLeftRadius: "26px",
                    boxShadow: "inset 0 0 0 1px rgba(60,97,139,0.15)",
                    flexDirection: "column",
                    alignItems: "center",
                    pt: 2.5,
                    pb: 2.5,
                }}
            >
                {/* Expand button with bell + total badge */}
                <Tooltip title={`Expand Alerts (${totalAlertsCount} total)`} placement="left">
                    <IconButton
                        onClick={handleToggleCollapse}
                        sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "white",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                            "&:hover": {
                                bgcolor: "#f5f5f5",
                                transform: "scale(1.06)",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.16)",
                            },
                            transition: "all 0.2s ease",
                        }}
                    >
                        <Badge
                            badgeContent={totalAlertsCount}
                            max={99}
                            sx={{
                                "& .MuiBadge-badge": {
                                    fontSize: "0.6rem",
                                    minWidth: "16px",
                                    height: "16px",
                                    backgroundColor: "#E53935",
                                    color: "white",
                                    fontWeight: 700,
                                    border: "2px solid white",
                                },
                            }}
                            invisible={totalAlertsCount === 0}
                        >
                            <Notifications sx={{ color: "#E53935", fontSize: 20 }} />
                        </Badge>
                    </IconButton>
                </Tooltip>

                <ChevronRight
                    onClick={handleToggleCollapse}
                    sx={{
                        color: "#8a94a6",
                        cursor: "pointer",
                        fontSize: 18,
                        mt: 0.5,
                        "&:hover": { color: "#E53935" },
                    }}
                />

                {/* Divider */}
                <Box sx={{ width: "26px", height: "1px", bgcolor: "rgba(60,97,139,0.25)", my: 1.5 }} />

                {/* Vertical "Alerts" label */}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                    <Typography
                        variant="caption"
                        sx={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            fontSize: "0.7rem",
                            fontWeight: "bold",
                            color: "#5a6b85",
                            letterSpacing: "2px",
                        }}
                    >
                        ALERTS
                    </Typography>
                </Box>

                {/* Divider */}
                <Box sx={{ width: "26px", height: "1px", bgcolor: "rgba(60,97,139,0.25)", my: 1.5 }} />

                {/* High priority indicator */}
                {highPriorityCount > 0 ? (
                    <Tooltip title={`${highPriorityCount} high priority`} placement="left">
                        <Box
                            sx={{
                                bgcolor: "#f44336",
                                color: "white",
                                borderRadius: "14px",
                                minWidth: 28,
                                height: 28,
                                px: 0.75,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                animation: "alertPulse 2s infinite",
                                "@keyframes alertPulse": {
                                    "0%": { boxShadow: "0 0 0 0 rgba(244,67,54,0.35)" },
                                    "70%": { boxShadow: "0 0 0 6px rgba(244,67,54,0)" },
                                    "100%": { boxShadow: "0 0 0 0 rgba(244,67,54,0)" },
                                },
                            }}
                        >
                            <Typography variant="caption" fontSize="0.68rem" fontWeight="bold">
                                {highPriorityCount}
                            </Typography>
                        </Box>
                    </Tooltip>
                ) : (
                    <Box sx={{ height: 28 }} />
                )}
            </Box>
        );
    }
    //handle the alert to be marked as read
    const handleAlertRead = async (alert: any, comments: any) => {
        if (alert.source === "Cordon") {
            const updatedIds = new Set(acknowledgedCordonIds);
            updatedIds.add(alert.id);
            localStorage.setItem(
                "acknowledgedCordonSosIds",
                Array.from(updatedIds).join("|"),
            );
            setAcknowledgedCordonIds(updatedIds);
            return;
        }

        const response = await modifyData(`/admin/alert/update-status/${alert.id}`, "put", {
            acknowledgedDate: moment().format("YYYY-MM-DD"),
            acknowledgedTime: moment().format("HH:mm"),
            comments: comments,
        });
        if (response.status) {
            fetchData();
        }
    };

    // Expanded view (original component)
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                minHeight: 0,
                bgcolor: "#d3ecff",
                borderRadius: 0,
                p: 2,
                boxSizing: "border-box",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                borderLeft: "1px solid #e0e0e0",
                transition: "width 0.3s ease",
            }}
        >
            {/* Header with collapse button */}
            {!hideHeader && <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" sx={{ color: "202224" }} fontWeight="bold">
                    {filter === "normal"
                        ? "Alerts"
                        : filter === "person-sos"
                          ? "Driver / Employee SOS"
                          : filter === "vehicle-sos"
                            ? "Cab / GPS SOS"
                            : "Safe Home"} ({totalAlertsCount})
                </Typography>

                <Tooltip title="Collapse Alerts Panel" placement="left">
                    <IconButton
                        onClick={onClose ?? handleToggleCollapse}
                        size="small"
                        sx={{
                            bgcolor: "white",
                            boxShadow: 1,
                            "&:hover": {
                                bgcolor: "#f5f5f5",
                                transform: "scale(1.05)",
                            },
                            transition: "all 0.2s ease",
                            marginRight: '25px'
                        }}
                    >
                        {onClose ? (
                            <Close sx={{ color: "#666" }} />
                        ) : (
                            <ChevronLeft sx={{ color: "#666" }} />
                        )}
                    </IconButton>
                </Tooltip>
            </Box>}

            {filter === "person-sos" && (
                <Button
                    variant={femaleEmployeeSosOnly ? "contained" : "outlined"}
                    color="secondary"
                    size="small"
                    onClick={() =>
                        setFemaleEmployeeSosOnly((showOnlyFemale) => !showOnlyFemale)
                    }
                    sx={{ alignSelf: "flex-start", mb: 1 }}
                >
                    {femaleEmployeeSosOnly
                        ? "Show all Driver / Employee SOS"
                        : "Only Female Employee SOS"}
                </Button>
            )}

            {/* Loading State */}
            {alertsLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {/* Error State */}
            {alertsError && (
                <MuiAlert severity="error" sx={{ mt: 1, fontSize: "0.75rem" }}>
                    Failed to load alerts
                </MuiAlert>
            )}

            {/* No Alerts State */}
            {!alertsLoading && !alertsError && displayedAlerts.length === 0 && (
                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        mt: 1,
                        bgcolor: "#e8f5e8",
                        textAlign: "center"
                    }}
                >
                    <Typography variant="body2" color="success.main" fontWeight="bold">
                        No active alerts
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        All systems running smoothly
                    </Typography>
                </Paper>
            )}

            {/* Scrollable container for alert items */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    mt: 1,
                    pr: 1,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {displayedAlerts.map((alert: AlertData) => (
                    <Paper
                        key={alert.id}
                        elevation={1}
                        sx={{
                            p: 1,
                            mb: 1,
                            bgcolor:
                                alert.type.toLowerCase() === "sos" &&
                                alert.source?.toLowerCase() === "employee" &&
                                getAlertGender(alert) === "female"
                                    ? "#f8bbd0"
                                    : alert.type.toLowerCase() === "sos"
                                      ? getPriorityColor(alert.priority)
                                      : "#ffffff",
                            border:
                                alert.type.toLowerCase() === "sos" &&
                                alert.source?.toLowerCase() === "employee" &&
                                getAlertGender(alert) === "female"
                                    ? "1px solid #e91e63"
                                    : alert.priority === "High"
                                      ? "1px solid #f44336"
                                      : "none",
                            cursor: "pointer",
                            transition: "transform 0.2s",
                            "&:hover": {
                                transform: "translateX(2px)",
                                boxShadow: 2
                            }
                        }}
                    >
                        {/* Header with priority and type */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" fontSize="0.8rem">
                                {alert.priority} Priority
                            </Typography>
                            <div>
                                {!isAcknowledged(alert) && (
                                    <Tooltip title="Acknowledge Alert" placement="top">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedAlert(alert);
                                                setComments("");
                                                setOpenDialog(true);
                                            }}
                                            sx={{ color: "#11a9e9", p: 0.5 }}
                                        >
                                            <MarkChatReadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Chip
                                    label={alert.type.toUpperCase()}
                                    size="small"
                                    color={getTypeChipColor(alert.type) as "error" | "warning" | "info" | "default"}
                                    sx={{ 
                                       fontSize: "0.6rem", 
                                       height: 18,
                                       ...(alert.type === "sos" || alert.type === "emergency" || alert.type === "breakdown" || alert.type === "accident"
                                         ? { backgroundColor: "#FF1943", color: "#ffffff" }
                                         : {}),
                                     }}
                                />
                            </div>

                        </Box>

                        {/* Alert message */}
                        <Typography
                            variant="body2"
                            fontSize="0.75rem"
                            sx={{ mb: 0.5, lineHeight: 1.3 }}
                        >
                            {alert.message}
                        </Typography>

                        {/* Source and time */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Chip
                                label={alert.source}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.6rem", height: 16 }}
                            />
                            <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                {formatTime(alert.createdAt)}
                            </Typography>
                        </Box>

                        {/* Location if available */}
                        {alert.location && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontSize="0.65rem"
                                sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
                            >
                                📍 {alert.location}
                            </Typography>
                        )}
                    </Paper>
                ))}
            </Box>

            {/* Show more indicator if there are many alerts */}
            {displayedAlerts.length >= 20 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 1,
                        mt: 1,
                        bgcolor: "rgba(0,0,0,0.05)",
                        textAlign: "center"
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        Showing latest alerts
                    </Typography>
                </Paper>
            )}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <Box
                    sx={{
                        position: "relative",
                        p: 0,
                    }}
                >
                    <IconButton
                        onClick={() => {
                            setOpenDialog(false);
                            setComments("");   
                            setErrors(false); 
                        }}
                        size="small"
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "grey.600",
                        }}
                    >
                        ✖
                    </IconButton>

                    <DialogTitle
                        sx={{
                            fontSize: "1rem",
                            fontWeight: "bold",
                            mb: 1,
                        }}
                    >Acknowledge Alert</DialogTitle>

                    <DialogContent>
                        <Typography variant="body1" sx={{ fontSize: "15px" }}>
                            Comments <span style={{ color: 'red' }} >*</span>
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            type="text"
                            fullWidth
                            multiline
                            placeholder="Enter comments"
                            variant="outlined"
                            value={comments}
                            // onChange={(e) => setComments(e.target.value)}
                            onChange={(e) => {
                                setComments(e.target.value);
                                if (e.target.value.trim() !== "") setErrors(false); // clear error on input
                            }}
                            required
                            error={errors}
                            helperText={errors ? "Comments are required" : ""}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Acknowledged Date: {moment().format("DD-MM-YYYY")} <br />
                            Acknowledged Time: {moment().format("HH:mm")}
                        </Typography>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            onClick={() => {
                                setOpenDialog(false);
                                setComments("");   // reset comments
                                setErrors(false);  // reset errors
                            }}
                            color="inherit">
                            Cancel
                        </Button>
                        <Button
                            // onClick={async () => {
                            //     if (!selectedAlert || comments.trim() === "") return;
                            //     await handleAlertRead(selectedAlert, comments);
                            //     setOpenDialog(false);
                            // }}
                            // color="primary"
                            // variant="contained"
                            onClick={async () => {
                                if (!selectedAlert) return;
                                // Validate comments
                                if (comments.trim() === "") {
                                    setErrors(true); // show error
                                    return;
                                }
                                setErrors(false); // clear error
                                await handleAlertRead(selectedAlert, comments);
                                setOpenDialog(false);
                                setComments(""); // reset after closing
                            }}
                            color="primary"
                            variant="contained"
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>


        </Box>
    );
};

export default AlertsSidebar;